import { createApi } from '@reduxjs/toolkit/query/react';
import {
  FormApproveRejectRequest,
  FormApproveRejectResponse,
  ResignationRequestFormRequest,
  ResignationRequestFormResponse,
} from '@Types/api/form';
import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { PAGE_SIZE, TABLE_PAGE_DEFAULT } from '@Features/constants';
import {
  ApprovalHistory,
  RequestListSearchRequest,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { customBaseQuery } from '@Utils/api/customBaseQuery';
import { approvalsListApi } from '@Store/api/approvalsListApiSlice';

export const resignationRequestApi = createApi({
  tagTypes: ['resignationRequest'],
  reducerPath: ApiReducerPath.API_RESIGNATION_REQUEST,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getResignationRequests: builder.mutation<
      RequestListSearchResponse<ResignationRequestFormResponse>,
      RequestListSearchRequest
    >({
      query: ({
        searchValue,
        filters = [],
        page = TABLE_PAGE_DEFAULT,
        size = PAGE_SIZE,
        sortBy = 'requestId',
        sortOrder = 'desc',
      }) => ({
        url: `${ApiBaseName.ESS}/resignation-request/search`,
        method: 'post',
        body: {
          searchValue,
          searchCriteriaList: [
            {
              searchCriterias: filters,
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),
    getResignationRequestForm: builder.query<
      ResignationRequestFormResponse,
      string
    >({
      query: (requestId) => ({
        url: `${ApiBaseName.ESS}/resignation-request`,
        method: 'get',
        params: { requestId },
      }),
    }),
    deleteResignationRequest: builder.mutation<null, string>({
      query: (requestId) => ({
        url: `${ApiBaseName.ESS}/resignation-request`,
        method: 'delete',
        params: { requestId },
      }),
    }),
    submitResignationRequest: builder.mutation<null, string>({
      query: (requestId) => ({
        url: `${ApiBaseName.ESS}/resignation-request/submit`,
        method: 'post',
        params: { requestId },
      }),
    }),
    cancelResignationRequest: builder.mutation<null, string>({
      query: (requestId) => ({
        url: `${ApiBaseName.ESS}/resignation-request/cancel`,
        method: 'post',
        params: { requestId },
      }),
    }),
    respondResignationRequest: builder.mutation<
      FormApproveRejectResponse,
      FormApproveRejectRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/resignation-request/command`,
        method: 'post',
        body,
      }),
      onQueryStarted: (_, api) => {
        api.queryFulfilled.then(() => {
          api.dispatch(
            approvalsListApi.util.invalidateTags(['taskList', 'widgetTaskList'])
          );
        });
      },
    }),
    saveDraftResignationRequest: builder.mutation<
      ResignationRequestFormResponse,
      ResignationRequestFormRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/resignation-request`,
        method: 'post',
        body,
      }),
    }),
    getSettlementAmount: builder.mutation<number, string>({
      query: (resignationDate) => ({
        url: `${ApiBaseName.ESS}/resignation-request/settlement-amount`,
        method: 'get',
        params: { resignationDate },
      }),
    }),
    getResignationApprovalHistory: builder.mutation<ApprovalHistory[], string>({
      query: (id) => ({
        url: `${ApiBaseName.ESS}/resignation-request/${id}/history`,
        method: 'get',
      }),
    }),
  }),
});

export const {
  useDeleteResignationRequestMutation,
  useSaveDraftResignationRequestMutation,
  useGetResignationRequestFormQuery,
  useSubmitResignationRequestMutation,
  useGetResignationRequestsMutation,
  useRespondResignationRequestMutation,
  useGetSettlementAmountMutation,
  useCancelResignationRequestMutation,
  useGetResignationApprovalHistoryMutation,
} = resignationRequestApi;
